import {ReactChild, useMemo} from 'react'
import {DefaultRecourse} from '../../types'
import {ResourceHeader} from './ResourceHeader'
import {ButtonTabProps, ButtonTabs} from './Tabs'
import {useStore} from '../../store'
import {withoutUnassignedShiftRows} from '../../helpers/generals'

interface WithResourcesProps {
  renderChildren(resource: DefaultRecourse): ReactChild
}
interface ResourceTablesProps extends WithResourcesProps {
  resources: DefaultRecourse[]
}
const WithResources = ({renderChildren}: WithResourcesProps) => {
  const {resourceViewMode, resources} = useStore()

  // These are the per-employee layouts (one tab/column per resource), so the
  // unassigned-shift rows the week/timeline grids pin on top don't belong here.
  const employeeResources = useMemo(() => withoutUnassignedShiftRows(resources), [resources])

  if (!employeeResources.length) return null
  if (resourceViewMode === 'tabs') {
    return <ResourcesTabTables resources={employeeResources} renderChildren={renderChildren} />
  } else {
    return <ResourcesTables resources={employeeResources} renderChildren={renderChildren} />
  }
}

const ResourcesTables = ({resources, renderChildren}: ResourceTablesProps) => {
  const {resourceFields, selectedPosition} = useStore()

  const filteredResources = resources.filter((res) => {
    if (selectedPosition === 'all') {
      return true
    }
    return res.position === selectedPosition
  })

  return (
    <>
      {filteredResources.map((res: DefaultRecourse, i: number) => (
        <div key={`${res[resourceFields.idField]}_${i}`}>
          <ResourceHeader resource={res} />
          {renderChildren(res)}
        </div>
      ))}
    </>
  )
}

const ResourcesTabTables = ({resources, renderChildren}: ResourceTablesProps) => {
  const {resourceFields, selectedResource, handleState} = useStore()

  const currentTabSafeId = useMemo(() => {
    const firstId = resources[0][resourceFields.idField]
    if (!selectedResource) {
      return firstId
    }
    // Make sure current selected id is within the resources array
    const idx = resources.findIndex((re) => re[resourceFields.idField] === selectedResource)
    if (idx < 0) {
      return firstId
    }

    return selectedResource
  }, [resources, selectedResource, resourceFields.idField])

  const tabs: ButtonTabProps[] = resources.map((res) => {
    if (currentTabSafeId === res[resourceFields.idField]) {
      return {
        id: res[resourceFields.idField],
        label: <ResourceHeader resource={res} />,
        component: <>{renderChildren(res)}</>,
      }
    } else {
      return {
        id: res[resourceFields.idField],
        label: <ResourceHeader resource={res} />,
        component: <></>,
      }
    }
  })

  const setTab = (tab: DefaultRecourse['assignee']) => {
    handleState(tab, 'selectedResource')
  }

  return (
    <ButtonTabs
      tabs={tabs}
      tab={currentTabSafeId}
      setTab={setTab}
      style={{display: 'grid', padding: '0px 0px'}}
    />
  )
}
WithResources.defaultProps = {
  span: 1,
}

export {WithResources}
